import React, { useState, useEffect } from 'react';
import {
  Container,
  VStack,
  HStack,
  Box,
  Text,
  Input,
  Alert,
  AlertIcon,
  AlertTitle,
  Heading,
  useToast,
  Spinner,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from '@chakra-ui/react';
import { POKER_COLORS } from '../theme/designSystem';
import PokerCard from '../components/ui/PokerCard';
import PokerButton from '../components/ui/PokerButton';
import PokerBadge from '../components/ui/PokerBadge';
import { auth } from '../firebase';
import stripeService from '../services/stripeService';

interface DepositBreakdown {
  usdAmount: number;
  pkCashCredit: number;
}

const DepositPage: React.FC = () => {
  const [usdAmount, setUsdAmount] = useState<string>('25');
  const [loading, setLoading] = useState(false);
  const [breakdown, setBreakdown] = useState<DepositBreakdown>({
    usdAmount: 25,
    pkCashCredit: 25,
  });
  const toast = useToast();

  useEffect(() => {
    const amount = parseFloat(usdAmount) || 0;
    setBreakdown({
      usdAmount: amount,
      pkCashCredit: amount, // 1:1 USD to PSS Coins
    });
  }, [usdAmount]);

  const handleDeposit = async () => {
    const amount = parseFloat(usdAmount);
    const currentUser = auth.currentUser;

    if (!currentUser) {
      toast({
        title: 'Not Logged In',
        description: 'Please log in to make a purchase',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!amount || amount < 10) {
      toast({
        title: 'Invalid Amount',
        description: 'Minimum purchase: $10.00',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (amount > 10000) {
      toast({
        title: 'Amount Too Large',
        description: 'Maximum purchase is $10,000',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    
    try {
      const result = await stripeService.depositPKCash(currentUser.uid, amount);
      
      if (!result.success) {
        throw new Error(result.error || 'Purchase failed');
      }
      
      toast({
        title: 'Redirecting to Checkout...',
        description: 'Please complete your payment',
        status: 'info',
        duration: 2000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Deposit error:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to process purchase',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxW="2xl" py={8} pl={{ base: 4, md: 24 }} pb={{ base: 24, md: 8 }}>
      <VStack spacing={6} align="stretch">
        <Box>
          <Heading color={POKER_COLORS.cyan} size="lg" mb={2}>
             Get Coins

          </Heading>
          <Text color="gray.400" fontSize="sm">
            Purchase PlayCoins + Get FREE PSS Coins
          </Text>
        </Box>

        {/* Info Alert */}
        <Alert status="info" rounded="lg" bg={`${POKER_COLORS.cyan}11`} borderColor={POKER_COLORS.cyan}>
          <AlertIcon color={POKER_COLORS.cyan} />
          <Box>
            <AlertTitle color={POKER_COLORS.cyan}>1:1 Conversion</AlertTitle>
            <Text color="gray.200" fontSize="sm" mt={1}>
              $1 USD = PlayCoins + FREE PSS Coins bonus. No fees. Instant processing for card payments.
            </Text>
          </Box>
        </Alert>

        {/* Deposit Methods */}
        <PokerCard glow={true}>
          <Tabs variant="soft-rounded">
            <TabList mb={6} gap={2}>
              <Tab color={POKER_COLORS.cyan} _selected={{ bg: POKER_COLORS.cyan, color: POKER_COLORS.darkTeal }}>
                 Card

              </Tab>
              <Tab color={POKER_COLORS.cyan} _selected={{ bg: POKER_COLORS.cyan, color: POKER_COLORS.darkTeal }}>
                 Bank

              </Tab>
            </TabList>

            <TabPanels>
              {/* Credit Card */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <Box p={3} bg={`${POKER_COLORS.darkTeal}33`} rounded="md" borderLeft={`3px solid ${POKER_COLORS.darkGreen}`}>
                    <Text color={POKER_COLORS.darkGreen} fontSize="sm" fontWeight="bold">
                      Limits: $10 - $5,000
                    </Text>
                    <Text color="gray.400" fontSize="xs" mt={1}>
                      Instant processing. Visa, Mastercard, Amex accepted.
                    </Text>
                  </Box>

                  <Text color={POKER_COLORS.cyan} fontWeight="bold">
                    Amount
                  </Text>
                  <HStack>
                    <Text color="gray.400" fontSize="lg">
                      $
                    </Text>
                    <Input
                      type="number"
                      placeholder="25.00"
                      value={usdAmount}
                      onChange={(e) => setUsdAmount(e.target.value)}
                      min="10"
                      max="5000"
                      step="0.01"
                      bg={`${POKER_COLORS.darkTeal}33`}
                      border={`2px solid ${POKER_COLORS.cyan}`}
                      color={POKER_COLORS.cyan}
                      _focus={{
                        boxShadow: `0 0 12px ${POKER_COLORS.cyan}`,
                      }}
                      fontSize="xl"
                      fontWeight="bold"
                    />
                  </HStack>

                  <PokerButton
                    variant="primary"
                    w="100%"
                    h="48px"
                    isLoading={loading}
                    onClick={handleDeposit}
                  >
                    {loading ? <Spinner size="sm" /> : `Purchase $${breakdown.usdAmount.toFixed(2)}`}
                  </PokerButton>
                </VStack>
              </TabPanel>

              {/* ACH Transfer */}
              <TabPanel>
                <VStack spacing={4} align="stretch">
                  <Box p={3} bg={`${POKER_COLORS.darkTeal}33`} rounded="md" borderLeft={`3px solid ${POKER_COLORS.accentCyan}`}>
                    <Text color={POKER_COLORS.accentCyan} fontSize="sm" fontWeight="bold">
                      Limits: $50 - $10,000
                    </Text>
                    <Text color="gray.400" fontSize="xs" mt={1}>
                      1-3 business days. Lower fees for large amounts.
                    </Text>
                  </Box>

                  <Text color={POKER_COLORS.cyan} fontWeight="bold">
                    Amount
                  </Text>
                  <HStack>
                    <Text color="gray.400" fontSize="lg">
                      $
                    </Text>
                    <Input
                      type="number"
                      placeholder="100.00"
                      value={usdAmount}
                      onChange={(e) => setUsdAmount(e.target.value)}
                      min="50"
                      max="10000"
                      step="0.01"
                      bg={`${POKER_COLORS.darkTeal}33`}
                      border={`2px solid ${POKER_COLORS.cyan}`}
                      color={POKER_COLORS.cyan}
                      _focus={{
                        boxShadow: `0 0 12px ${POKER_COLORS.cyan}`,
                      }}
                      fontSize="xl"
                      fontWeight="bold"
                    />
                  </HStack>

                  <PokerButton
                    variant="secondary"
                    w="100%"
                    h="48px"
                    isLoading={loading}
                    onClick={handleDeposit}
                  >
                    {loading ? <Spinner size="sm" /> : `Initiate ACH - $${breakdown.usdAmount.toFixed(2)}`}
                  </PokerButton>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </PokerCard>

        {/* Breakdown */}
        <PokerCard>
          <VStack spacing={3} align="stretch">
            <Text color={POKER_COLORS.cyan} fontWeight="bold">
              Summary
            </Text>

            <HStack justify="space-between" p={2}>
              <Text color="gray.400">Amount</Text>
              <Text color="white" fontWeight="bold">
                ${breakdown.usdAmount.toFixed(2)}
              </Text>
            </HStack>

            <HStack justify="space-between" p={2} bg={`${POKER_COLORS.darkGreen}22`} rounded="md">
              <Text color={POKER_COLORS.darkGreen}>You Receive</Text>
              <HStack>
                <Text color={POKER_COLORS.darkGreen} fontWeight="bold">
                  ${breakdown.pkCashCredit.toFixed(2)}
                </Text>
                <PokerBadge type="cash">PSS Coins</PokerBadge>
              </HStack>
            </HStack>
          </VStack>
        </PokerCard>

        {/* Footer */}
        <Box p={4} bg={`${POKER_COLORS.darkTeal}44`} rounded="md" textAlign="center">
          <Text color="gray.400" fontSize="xs">
             All transactions are encrypted. Stripe handles payments securely.

          </Text>
        </Box>
      </VStack>
    </Container>
  );
};

export default DepositPage;